/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  FormArray,
} from '@angular/forms';
import { Component, OnInit, Input, forwardRef, SimpleChanges, OnChanges } from '@angular/core';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';

const customValueProvider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiSelectChecklistComponent),
  multi: true,
};
const customValidateProvider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => MultiSelectChecklistComponent),
  multi: true,
};

@Component({
    selector: 'multi-select-checklist',
    templateUrl: './multi-select-checklist.component.html',
    styleUrls: ['./multi-select-checklist.component.scss'],
    providers: [customValueProvider, customValidateProvider],
    standalone: false
})

export class MultiSelectChecklistComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label: string;
  @Input() formTouched: boolean;
  @Input() dataList: any[];
  @Input() columns: number = 1;  // default: single column

  dataForm: FormGroup;

  writtenValue;
  constructor(private fb: FormBuilder, private formHelperService: ReactiveFormHelperService) {
    this.dataForm = fb.group({
      checklistItems: [fb.array([])],
    });

    this.dataForm.valueChanges.subscribe(() => this.onFormChanged());
  }

  ngOnChanges({ formTouched }: SimpleChanges): void {
    if (formTouched && formTouched.currentValue) {
      this.formHelperService.markFormControlTouched(this.dataForm);
    }

    this.dataForm.controls['checklistItems'] = this.buildCheckList();
    this.setControlsFromWrittenValue();
  }

  buildCheckList() {
    if (!this.dataList) {
      return this.fb.array([]);
    }

    const arr = this.dataList.map((option) => {
      return this.fb.control(false);
    });
    return this.fb.array(arr);
  }

  // shortcuts to controls for readability
  get checklistItems(): AbstractControl {
    return this.dataForm.controls['checklistItems'];
  }
  get checklistItemsArray(): FormArray {
    return this.dataForm.get('checklistItems') as FormArray;
  }

  ngOnInit(): void {}

  // required to propogate the changes to the consuming form
  onFormChanged() {
    this.propagateChange(this.getChecklistItems());
    this.onTouched();
  }

  getChecklistItems(): number[] {
    const formArrayValues = this.checklistItemsArray.value;
    const selectedIds: number[] = [];

    for (let i = 0, len = formArrayValues.length; i < len; i++) {
      if (formArrayValues[i] === true) {
        selectedIds.push(this.dataList[i].id);
      }
    }

    return selectedIds;
  }

  checkboxChanged() {
    this.propagateChange(this.getChecklistItems());
    this.onTouched();
  }

  propagateChange: any = () => {};
  onTouched: any = () => {
    console.log('multi select touched');
  };

  writeValue(ids: any[]): void {
    this.writtenValue = ids;
    this.setControlsFromWrittenValue();
  }

  private setControlsFromWrittenValue() {
    const ids = this.writtenValue;
    if (ids && this.dataList && this.checklistItemsArray.controls.length > 0) {
      this.checklistItemsArray.controls.forEach((control) => {
        control.setValue(false);
      });
      ids.forEach((id) => {
        const index = this.dataList.findIndex((f) => f.id === id);
        this.checklistItemsArray.controls[index].setValue(true);
      });
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  getRows(): number {
  // Ensure at least one row; ceil ensures all items fit
  return Math.ceil(this.dataList?.length / this.columns || 1);
}

  public validate(c: FormControl) {
    // console.log(`Validating multi-select-checklist: valid = ${this.dataForm.valid}`);
    return null;
    // TODO: [Other][DPB][2020-05-16] Add validation here if required
    // return this.dataForm.valid
    //   ? null
    //   : {
    //       formInvalid: {
    //         valid: false,
    //       },
    //     };
  }
}
